import moment from 'moment/moment'

import { PSApplication } from '../components/app/PSApplication'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { Feature } from './Feature'
import { Industry } from './Industry'
import { FKType } from './SQLBuilder'

type AliasConstructorArgs = Omit<ConstructorArgs<() => Alias>, 'percent_growth'> & {
    pct_growth: string | number | null
}

export class Alias extends BaseRecord {
    readonly recordType = 'alias'

    private static states: any = {}
    alias: string | undefined
    owner: string | undefined
    allowPost: string | undefined
    firstClient: string | undefined
    proudestMoment: string | undefined
    adoption: string | undefined
    clientType: string | undefined
    mainGoal: string | undefined
    topPriority: string | undefined
    conversionStrategy: string | undefined
    referralStrategy: string | undefined
    successMetric: string | undefined
    favoriteBook: string | undefined
    openQuestion: string | undefined

    readonly plan: string | undefined
    readonly city: string | undefined
    readonly state: string | undefined
    readonly follow: boolean | undefined = false
    readonly badges: string | undefined
    readonly income: number | undefined
    readonly percentGrowth: number | undefined
    readonly status: string | undefined
    readonly hours: number = 0
    readonly streak: string | undefined
    readonly streakLength: number = 0
    readonly signupDate: moment.Moment | undefined
    readonly lastModifiedDate: moment.Moment | undefined

    constructor(data?: Partial<AliasConstructorArgs>) {
        super(data)
        if (!data) {
            this.owner = PSApplication.me().id
            return
        }

        this.alias = data.alias ?? undefined
        this.owner = data.owner ?? undefined
        this.allowPost = data.allow_post ?? undefined
        this.firstClient = data.first_client ?? undefined
        this.proudestMoment = data.proudest_moment ?? undefined
        this.adoption = data.adoption ?? undefined
        this.clientType = data.client_type ?? undefined
        this.mainGoal = data.main_goal ?? undefined
        this.topPriority = data.top_priority ?? undefined
        this.conversionStrategy = data.conversion_strategy ?? undefined
        this.referralStrategy = data.referral_strategy ?? undefined
        this.successMetric = data.success_metric ?? undefined
        this.favoriteBook = data.favorite_book ?? undefined
        this.openQuestion = data.open_question ?? undefined

        this.plan = data.plan ?? undefined
        this.city = data.city ?? undefined
        this.state = data.state ?? undefined
        this.follow = data.follow ?? false
        this.badges = data.badges ?? undefined
        this.income = this.propertyFromCurrencyArg(data.income)
        this.percentGrowth = this.propertyFromNumberArg(data.pct_growth)
        this.status = data.status ?? undefined
        this.hours = this.propertyFromIntArg(data.hours) ?? 0
        this.streak = data.streak ?? undefined
        this.streakLength = this.propertyFromIntArg(data.streak_length) ?? 0
        this.signupDate = this.propertyFromTimestampArg(data.signup_date)
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
    }

    clone(overrides?: Partial<AliasConstructorArgs>): Alias {
        return new Alias({
            ...this.serializeForClone(),
            pct_growth: this.percentGrowth,
            ...overrides,
        })
    }

    name(): string {
        return !!this.alias ? this.alias : this.industry!.title!
    }

    numberOfBadges(): number {
        return (this.badges ?? '').split(',').length
    }

    allowPosting(): boolean {
        return ['diamond', 'gold', 'silver', 'bronze'].includes(this.status || '') && !!this.plan
    }

    scheduleString(): string {
        return this.hours > 30 ? 'Full time' : 'Part time'
    }

    locationScheduleString(): string {
        if (Object.keys(Alias.states).length == 0) {
            Alias.states = {
                AL: 'Alabama',
                AK: 'Alaska',
                AZ: 'Arizona',
                AR: 'Arkansas',
                CA: 'California',
                CO: 'Colorado',
                CT: 'Connecticut',
                DC: 'Washington DC',
                DE: 'Delaware',
                FL: 'Florida',
                GA: 'Georgia',
                HI: 'Hawaii',
                ID: 'Idaho',
                IL: 'Illinois',
                IN: 'Indiana',
                IA: 'Iowa',
                KS: 'Kansas',
                KY: 'Kentucky',
                LA: 'Louisiana',
                ME: 'Maine',
                MD: 'Maryland',
                MA: 'Massachusetts',
                MI: 'Michigan',
                MN: 'Minnesota',
                MS: 'Mississippi',
                MO: 'Missouri',
                MT: 'Montana',
                NE: 'Nebraska',
                NV: 'Nevada',
                NH: 'New Hampshire',
                NJ: 'New Jersey',
                NM: 'New Mexico',
                NY: 'New York',
                NC: 'North Carolina',
                ND: 'North Dakota',
                OH: 'Ohio',
                OK: 'Oklahoma',
                OR: 'Oregon',
                PA: 'Pennsylvania',
                RI: 'Rhode Island',
                SC: 'South Carolina',
                SD: 'South Dakota',
                TN: 'Tennessee',
                TX: 'Texas',
                UT: 'Utah',
                VT: 'Vermont',
                VA: 'Virginia',
                WA: 'Washington',
                WV: 'West Virginia',
                WI: 'Wisconsin',
                WY: 'Wyoming',
            }
        }
        return `${Alias.states[this.state || '']} (${this.scheduleString()})`
    }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.feature) this.feature = BaseRecord.fromSerializer(data.feature, Feature)
    }

    static fk: FKType = {}

    jsonObject() {
        let dict = super.jsonObject()
        dict['alias'] = this.alias
        dict['allow_post'] = this.allowPost
        dict['first_client'] = this.firstClient
        dict['proudest_moment'] = this.proudestMoment
        dict['adoption'] = this.adoption
        dict['client_type'] = this.clientType
        dict['main_goal'] = this.mainGoal
        dict['top_priority'] = this.topPriority
        dict['conversion_strategy'] = this.conversionStrategy
        dict['referral_strategy'] = this.referralStrategy
        dict['success_metric'] = this.successMetric
        dict['favorite_book'] = this.favoriteBook
        dict['open_question'] = this.openQuestion
        dict['feature'] = this.feature != undefined ? this.feature.id : ''
        return dict
    }

    static dataType(): DataType {
        return 'aliases' as DataType
    }

    get feature(): Feature | undefined {
        return this.getForeignRecord('feature', Feature)
    }
    set feature(feature: Feature | undefined) {
        this.setForeignRecord('feature', feature)
    }
    get channel(): Industry | undefined {
        return this.getForeignRecord('channel', Industry)
    }
    set channel(channel: Industry | undefined) {
        this.setForeignRecord('channel', channel)
    }
    get industry(): Industry | undefined {
        return this.getForeignRecord('industry', Industry)
    }
    set industry(industry: Industry | undefined) {
        this.setForeignRecord('industry', industry)
    }
}
